import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE } from '../../config';

interface ReviewItem {
  productId: string;
  userId: string;
  displayName?: string;
  productName?: string;
  reviewText: string;
  ratingValue?: number;
  buzzScore?: number;
  upOrDown?: string;
  createdAt: string;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function AdminReviews() {
  const { authFetch } = useAuth();
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await authFetch(`${API_BASE}/v1/admin/reviews`);
        if (res.ok) {
          const json = await res.json();
          setReviews(json.data || []);
        }
      } catch { /* */ }
      finally { setLoading(false); }
    }
    fetchReviews();
  }, [authFetch]);

  const totalReviews = reviews.length;
  const last7Days = reviews.filter((r) => new Date(r.createdAt) > new Date(Date.now() - 7 * 86400000)).length;
  const avgPerWeek = totalReviews > 0 ? (last7Days).toFixed(1) : '0';

  if (loading) return <div className="admin-loading">Loading reviews...</div>;

  return (
    <div>
      <h1>Reviews</h1>

      <div className="admin-stats" style={{ marginBottom: '24px' }}>
        <div className="admin-stat-card">
          <div className="stat-label">Total Reviews</div>
          <div className="stat-value">{totalReviews}</div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-label">Last 7 Days</div>
          <div className="stat-value">{last7Days}</div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-label">Avg / Week</div>
          <div className="stat-value">{avgPerWeek}</div>
        </div>
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Product</th>
              <th>Rating</th>
              <th>Review</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review, i) => (
              <tr key={`${review.userId}-${review.productId}-${i}`}>
                <td style={{ whiteSpace: 'nowrap' }}>{review.displayName || 'Anonymous'}</td>
                <td>
                  <Link
                    to={`/products/${review.productId}`}
                    style={{ color: 'var(--color-primary)', fontSize: '0.85rem' }}
                  >
                    {review.productName || review.productId.slice(0, 8) + '...'}
                  </Link>
                </td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                  {review.ratingValue ? `★${review.ratingValue}` : ''}
                  {review.buzzScore ? ` ⚡${review.buzzScore}` : ''}
                  {review.upOrDown ? ` ${review.upOrDown === 'Up' ? '↑' : review.upOrDown === 'Down' ? '↓' : '↕'}` : ''}
                </td>
                <td style={{ fontSize: '0.85rem', maxWidth: '300px' }}>
                  {review.reviewText.length > 80 ? review.reviewText.slice(0, 80) + '...' : review.reviewText}
                </td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  {timeAgo(review.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
